/**
 * Per-session client state in sessionStorage. Centralises the key
 * naming + the read/write API so AgentConsole, CustomerJoin, and
 * Chatroom all agree on the contract (DRY).
 *
 * Why sessionStorage and not localStorage:
 *   The wsToken is room-scoped + short-lived. sessionStorage clears
 *   on tab close, which matches the lifecycle of a single chat
 *   session. Localstorage would survive too long.
 *
 * Why not a context provider:
 *   The chat URL carries `sessionId`; the room can be opened directly
 *   (back/forward, refresh) so we need a side channel that survives
 *   tab refresh. A context wouldn't.
 */

export interface ChatSessionState {
  readonly wsToken: string
  readonly subscriberName: string
  readonly agentDisplayName: string
  /** UTC ms epoch — used by SessionCountdown for the live timer. */
  readonly expiresAt: number
}

/** Read the per-session state. Returns `null` if any required key is missing. */
export function loadChatSession(sessionId: string): ChatSessionState | null {
  const wsToken = sessionStorage.getItem(`ws:${sessionId}`)
  const subscriberName = sessionStorage.getItem(`sub:${sessionId}`)
  const agentDisplayName = sessionStorage.getItem(`agent:${sessionId}`) ?? ''
  const expRaw = sessionStorage.getItem(`exp:${sessionId}`)
  if (!wsToken || subscriberName === null) return null
  return {
    wsToken,
    subscriberName,
    agentDisplayName,
    // Parse defensively — if storage was corrupted, fall back to "now"
    // which immediately renders "Expired" and lets the WS layer take
    // over with `room.expired`. Better than NaN propagation.
    expiresAt: expRaw ? Number.parseInt(expRaw, 10) || Date.now() : Date.now(),
  }
}

export interface ChatSessionInput {
  readonly wsToken: string
  readonly subscriberName: string
  readonly agentDisplayName: string | null
  readonly expiresAt: number
}

/** Write the per-session state at handshake time. */
export function saveChatSession(sessionId: string, state: ChatSessionInput): void {
  sessionStorage.setItem(`ws:${sessionId}`, state.wsToken)
  sessionStorage.setItem(`sub:${sessionId}`, state.subscriberName)
  sessionStorage.setItem(`agent:${sessionId}`, state.agentDisplayName ?? '')
  sessionStorage.setItem(`exp:${sessionId}`, String(state.expiresAt))
}
